import { gql, useQuery } from "@apollo/client";
import {
  ActualRow_employee,
  ActualRow_compCycleData as CompCycleData,
  GetLazyReportingActualTierData,
} from "../../../../__generated__/graphql";
import { CompCycleDisplay } from "../../../../components/CompCycle/types";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { IndentationCells } from "../../Budgets/Table/Connectors";
import { SkeletonRow } from "../../Budgets/Table/SkeletonRow";
import { ReportingActualTier } from "./ReportingActualTier";

type Props = {
  employee: ActualRow_employee;
  organizationName: string;
  varianceDisplay: CompCycleDisplay;
  compCycleData: CompCycleData;
  indentation: IndentationCells;
};
export function LazyReportingActualTier({
  employee,
  organizationName,
  varianceDisplay,
  compCycleData,
  indentation,
}: Props): JSX.Element {
  const { defaultCurrencyCode } = useCurrencies();

  const { loading, data } = useQuery<GetLazyReportingActualTierData>(
    LazyReportingActualTier.query(),
    {
      variables: {
        compCycleId: compCycleData.id,
        employeeId: employee.id,
        currencyCode: defaultCurrencyCode,
      },
      pollInterval: 10_000,
    }
  );

  if (loading) {
    return <SkeletonRow subComponents={3} indentation={indentation} />;
  }

  if (!data || !data.compCycle) {
    return (
      <tr>
        <td>Error</td>
      </tr>
    );
  }

  return (
    <ReportingActualTier
      rootBudget={data.compCycleBudget}
      manager={employee}
      reports={data.employees}
      organizationName={organizationName}
      compCycleData={compCycleData}
      varianceDisplay={varianceDisplay}
      indentation={indentation}
    />
  );
}

LazyReportingActualTier.query = () => gql`
  ${ReportingActualTier.fragments.reports}
  ${ReportingActualTier.fragments.compCycleBudget}
  ${ReportingActualTier.fragments.compCycleData}
  query GetLazyReportingActualTierData(
    $compCycleId: Int!
    $employeeId: Int!
    $currencyCode: CurrencyCode!
  ) {
    compCycleBudget(compCycleId: $compCycleId, employeeId: $employeeId) {
      compCycleId
      employeeId
      ...ReportingActualTier_compCycleBudget
    }
    employees(managerId: $employeeId) {
      ...ReportingActualTier_reports
    }
    compCycle(id: $compCycleId) {
      id
      ...ReportingActualTier_compCycleData
    }
  }
`;
